import React, { useState } from 'react';
import { EQUIPMENT, EXPERIENCE_LEVELS, PLAN_GOALS, errorText, formatTarget } from './strengthFormat';

const PREFS_KEY = 'kosmoholm-strength-ai-prefs';

const DEFAULT_PREFS = {
  goal: 'GeneralFitness',
  daysPerWeek: 3,
  minutesPerSession: 45,
  experience: 'Beginner',
  fullGym: true,
  equipment: ['Dumbbell', 'Bench'],
  focus: ''
};

function loadPrefs() {
  try {
    return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') };
  } catch {
    return DEFAULT_PREFS;
  }
}

function AiCoach({ api, t, lang, catalogByKey, showMessage, onSaved, onEdit, onCancel }) {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);

  const set = (field, value) => setPrefs(p => ({ ...p, [field]: value }));
  const toggleEquipment = (item) =>
    set('equipment', prefs.equipment.includes(item) ? prefs.equipment.filter(e => e !== item) : [...prefs.equipment, item]);

  const generate = async (e) => {
    e?.preventDefault();
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    setGenerating(true);
    setError(null);
    try {
      setDraft(await api.generatePlan({
        goal: prefs.goal,
        daysPerWeek: prefs.daysPerWeek,
        minutesPerSession: prefs.minutesPerSession,
        experience: prefs.experience,
        // No equipment listed means a full gym; bodyweight is always available.
        equipment: prefs.fullGym ? [] : prefs.equipment,
        focus: prefs.focus || null
      }));
    } catch (err) {
      // Server messages are English; the coach speaks the user's language.
      setError(/not configured/i.test(err?.message || '') ? t('strength.ai.notConfigured') : t('strength.ai.failed'));
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.createPlan(draft.plan);
      showMessage(t('strength.plans.saved'));
      await onSaved();
    } catch (err) {
      showMessage(errorText(err, t('strength.errors.save')), 'error');
      setSaving(false);
    }
  };

  if (draft) {
    const { plan, droppedExercises = [] } = draft;
    return (
      <div className="st-card st-ai-draft">
        <span className="st-eyebrow">✨ {t('strength.ai.draft')}</span>
        <h2>{plan.name}</h2>
        {plan.description && <p>{plan.description}</p>}
        {plan.aiRationale && <p className="st-rationale-text">{plan.aiRationale}</p>}

        <div className="st-days">
          {plan.days.map((day, i) => (
            <div key={i} className="st-day">
              <h4>{day.name}</h4>
              {day.notes && <p className="st-muted">{day.notes}</p>}
              <ul className="st-day-exercises">
                {day.exercises.map((e, j) => (
                  <li key={j}>
                    <span>{catalogByKey[e.exerciseKey]?.name || e.exerciseKey}</span>
                    <span className="st-muted">{formatTarget(e, lang)}</span>
                    {e.notes && <span className="st-cue">💡 {e.notes}</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {droppedExercises.length > 0 && (
          <p className="st-muted st-small">{t('strength.ai.dropped', { count: droppedExercises.length })}</p>
        )}
        <p className="st-muted st-small">{t('strength.ai.noWeightsHint')}</p>

        <div className="st-row st-row-end">
          <button type="button" className="st-btn" onClick={() => setDraft(null)}>{t('strength.ai.again')}</button>
          <button type="button" className="st-btn" onClick={() => onEdit(plan)}>{t('strength.ai.editFirst')}</button>
          <button type="button" className="st-btn st-btn-primary" disabled={saving} onClick={save}>{t('strength.ai.save')}</button>
        </div>
      </div>
    );
  }

  return (
    <form className="st-card st-ai" onSubmit={generate}>
      <h2>✨ {t('strength.ai.title')}</h2>
      <p className="st-muted">{t('strength.ai.intro')}</p>

      <div className="st-form-grid">
        <label className="st-field">
          <span>{t('strength.editor.goal')}</span>
          <select className="st-select" value={prefs.goal} onChange={(e) => set('goal', e.target.value)}>
            {PLAN_GOALS.map(g => <option key={g} value={g}>{t(`strength.goals.${g}`)}</option>)}
          </select>
        </label>
        <label className="st-field">
          <span>{t('strength.editor.daysPerWeek')}</span>
          <select className="st-select" value={prefs.daysPerWeek} onChange={(e) => set('daysPerWeek', Number(e.target.value))}>
            {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="st-field">
          <span>{t('strength.ai.minutes')}</span>
          <select className="st-select" value={prefs.minutesPerSession} onChange={(e) => set('minutesPerSession', Number(e.target.value))}>
            {[20, 30, 45, 60, 75, 90].map(n => <option key={n} value={n}>{n} min</option>)}
          </select>
        </label>
        <label className="st-field">
          <span>{t('strength.ai.experience')}</span>
          <select className="st-select" value={prefs.experience} onChange={(e) => set('experience', e.target.value)}>
            {EXPERIENCE_LEVELS.map(x => <option key={x} value={x}>{t(`strength.experience.${x}`)}</option>)}
          </select>
        </label>
      </div>

      <fieldset className="st-fieldset">
        <legend>{t('strength.ai.where')}</legend>
        <label className="st-check-label">
          <input type="radio" name="st-where" checked={prefs.fullGym} onChange={() => set('fullGym', true)} />
          {t('strength.ai.fullGym')}
        </label>
        <label className="st-check-label">
          <input type="radio" name="st-where" checked={!prefs.fullGym} onChange={() => set('fullGym', false)} />
          {t('strength.ai.homeGym')}
        </label>
        {!prefs.fullGym && (
          <div className="st-chips">
            {EQUIPMENT.map(item => (
              <label key={item} className={`st-chip ${prefs.equipment.includes(item) ? 'active' : ''}`}>
                <input type="checkbox" className="st-sr-only" checked={prefs.equipment.includes(item)} onChange={() => toggleEquipment(item)} />
                {t(`strength.equipment.${item}`)}
              </label>
            ))}
          </div>
        )}
      </fieldset>

      <label className="st-field">
        <span>{t('strength.ai.focus')}</span>
        <textarea
          className="st-input"
          rows={3}
          maxLength={500}
          value={prefs.focus}
          placeholder={t('strength.ai.focusPlaceholder')}
          onChange={(e) => set('focus', e.target.value)}
        />
      </label>

      {error && <p className="st-error" role="alert">{error}</p>}
      <p className="st-muted st-small">{t('strength.ai.privacy')}</p>

      <div className="st-row st-row-end">
        <button type="button" className="st-btn" onClick={onCancel}>{t('strength.common.cancel')}</button>
        <button type="submit" className="st-btn st-btn-ai" disabled={generating}>
          {generating ? t('strength.ai.thinking') : `✨ ${t('strength.ai.generate')}`}
        </button>
      </div>
    </form>
  );
}

export default AiCoach;
